import { Select } from '@uag/react-core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useGetInputBindingSlot,
    type BindingEditorProps
} from 'app/modules/wallforming/components/input-binding/hooks/useGetInputBindingSlot';
import { inputActions } from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { InputDevice } from 'app/modules/wallforming/components/input-binding/types/inputDevice';
import { InputModifier } from 'app/modules/wallforming/components/input-binding/types/inputModifier';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import { MouseButton } from 'app/modules/wallforming/components/input-binding/types/mouseButton';
import {
    WHEEL_CODE,
    formatChord,
    getChordDevice,
    mouseCode,
    parseChord
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { TranslationKey } from 'app/modules/wallforming/components/input-binding/types/translationKey';

import 'app/modules/wallforming/custom-styles/customSelectStyle.css';

const NONE = 'none';

const buttonLabelKeys: Record<MouseButton, TranslationKey> = {
    [MouseButton.Left]: 'labelInputButtonLeft',
    [MouseButton.Middle]: 'labelInputButtonMiddle',
    [MouseButton.Right]: 'labelInputButtonRight'
};

const triggerLabelKeys: Record<InputTrigger, TranslationKey> = {
    [InputTrigger.Click]: 'labelInputTriggerClick',
    [InputTrigger.DoubleClick]: 'labelInputTriggerDoubleClick',
    [InputTrigger.Drag]: 'labelInputTriggerDrag'
};

const selectProps = { size: 'xs', comboboxProps: { zIndex: 20000 }, allowDeselect: false } as const;

interface GestureChange {
    code?: string;
    modifier?: string;
    trigger?: string;
}

/** Edits pointer gestures through selects restricted to the devices and triggers the action allows. */
export const PointerGestureEditor = ({
    actionId,
    slot,
    showClickType = false,
    onPendingChange
}: BindingEditorProps & {
    showClickType?: boolean;
    onPendingChange?: (hasPendingValue: boolean) => void;
}) => {
    const { t } = useTranslation();
    const { devices, triggers, mouseButtons, modifierOnly } = inputActions[actionId];
    const { chord } = useGetInputBindingSlot(actionId, slot);
    const { assignInputBinding } = usePlacementStore.getState();
    const parsed = chord ? parseChord(chord) : null;
    const defaultTrigger = triggers.includes(InputTrigger.Click) ? InputTrigger.Click : (triggers[0] ?? NONE);
    const [modifierDraft, setModifierDraft] = useState<string>(parsed?.modifiers[0] ?? NONE);
    const [triggerDraft, setTriggerDraft] = useState<string>(parsed?.trigger ?? defaultTrigger);

    const { buttonOptions, modifierOptions, triggerOptions } = useMemo(
        () => ({
            buttonOptions: [
                { value: NONE, label: t('labelInputButtonNone') },
                ...(devices.includes(InputDevice.Mouse)
                    ? Object.entries(buttonLabelKeys)
                          .filter(([button]) => !mouseButtons || mouseButtons.includes(Number(button) as MouseButton))
                          .map(([button, labelKey]) => ({
                              value: mouseCode(Number(button)),
                              label: t(labelKey)
                          }))
                    : []),
                ...(devices.includes(InputDevice.Wheel)
                    ? [{ value: WHEEL_CODE, label: t('labelInputButtonWheel') }]
                    : [])
            ],
            modifierOptions: [
                { value: NONE, label: t('labelInputModifierNone') },
                ...Object.values(InputModifier).map((modifier) => ({ value: modifier, label: modifier }))
            ],
            triggerOptions: triggers.length
                ? triggers.map((trigger) => ({ value: trigger, label: t(triggerLabelKeys[trigger]) }))
                : [{ value: NONE, label: t('labelInputTriggerNotApplicable') }]
        }),
        [devices, mouseButtons, t, triggers]
    );

    const update = (change: GestureChange) => {
        const modifier = change.modifier ?? parsed?.modifiers[0] ?? modifierDraft;
        const modifiers = modifier === NONE ? [] : [modifier as InputModifier];
        const trigger = change.trigger ?? parsed?.trigger ?? triggerDraft;
        setModifierDraft(modifier);
        setTriggerDraft(trigger);
        const code = change.code ?? parsed?.code ?? (modifierOnly ? mouseCode(MouseButton.Left) : NONE);
        if (code === NONE) {
            if (change.code === NONE) {
                setModifierDraft(NONE);
                setTriggerDraft(defaultTrigger);
                onPendingChange?.(false);
                assignInputBinding(actionId, slot, null);
            } else {
                onPendingChange?.(modifiers.length > 0 || trigger !== defaultTrigger);
            }
            return;
        }
        if (modifierOnly && modifier === NONE) {
            setModifierDraft(NONE);
            setTriggerDraft(defaultTrigger);
            onPendingChange?.(false);
            assignInputBinding(actionId, slot, null);
            return;
        }
        onPendingChange?.(modifiers.length > 0 || trigger !== defaultTrigger);
        assignInputBinding(
            actionId,
            slot,
            formatChord({
                code,
                modifiers,
                trigger:
                    getChordDevice(code) !== InputDevice.Mouse
                        ? undefined
                        : (triggers.find((allowed) => allowed === trigger) ?? triggers[0])
            })
        );
    };

    return (
        <>
            {modifierOnly ? (
                <div aria-hidden="true" />
            ) : (
                <Select
                    {...selectProps}
                    className="custom-select mouse-button-select w-full min-w-0"
                    classNames={{ dropdown: 'mouse-button-dropdown' }}
                    data={buttonOptions}
                    value={parsed?.code ?? NONE}
                    onChange={(code) => code && update({ code })}
                />
            )}
            <Select
                {...selectProps}
                className="custom-select modifier-select w-full min-w-0"
                classNames={{ dropdown: 'modifier-dropdown' }}
                data={modifierOptions}
                value={parsed?.modifiers[0] ?? modifierDraft}
                onChange={(modifier) => modifier && update({ modifier })}
            />
            {showClickType && (
                <Select
                    {...selectProps}
                    className="custom-select click-type-select w-full min-w-0"
                    classNames={{ dropdown: 'click-type-dropdown' }}
                    data={triggerOptions}
                    value={parsed?.trigger ?? triggerDraft}
                    onChange={(trigger) => trigger && trigger !== NONE && update({ trigger })}
                />
            )}
        </>
    );
};
